import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, LayoutDashboard, Search } from 'lucide-react';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { PRE_LAUNCH_MODE } from '../config/preLaunch';

const Navbar = ({ user, onLogout }) => {
  const { total: unreadTotal } = useUnreadMessages();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  // Show navigation if pre-launch mode is disabled OR if user is an admin
  const showNavigation = !PRE_LAUNCH_MODE || (user && user.role === 'admin');

  const navLinks = [
    { path: '/for-customers', label: 'Feiernde', testId: 'nav-customers' },
    { path: '/inspiration', label: 'Inspiration', testId: 'nav-inspiration' },
    { path: '/for-providers', label: 'Eventpros', testId: 'nav-providers' },
    { path: '/pricing', label: 'Preise', testId: 'nav-pricing' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#2B2142] backdrop-blur-md shadow-sm">
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo - Left. Shrinks to absorb overflow so the hamburger is never clipped on mobile */}
          <div
            className="min-w-0 flex items-center overflow-hidden cursor-pointer"
            onClick={() => navigate('/')}
            data-testid="navbar-logo"
          >
            <img
              src="/images/ec-logo.svg"
              alt="event connect"
              className="h-6 sm:h-7 w-auto min-w-0 max-w-full object-contain"
            />
          </div>

          {/* Desktop Navigation - Center (hidden in pre-launch mode, except for admins) */}
          {showNavigation && (
            <div className="hidden lg:flex items-center justify-center flex-1 gap-6">
              {navLinks.map((link) => (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className={`text-base transition-all whitespace-nowrap relative group ${
                    isActive(link.path) ? 'text-peach font-medium' : 'text-peach/80 hover:text-peach'
                  }`}
                  data-testid={link.testId}
                >
                  {link.label}
                  {/* Active indicator - thin underline */}
                  {isActive(link.path) && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-peach rounded-full"></span>
                  )}
                  {/* Hover indicator */}
                  {!isActive(link.path) && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-peach rounded-full scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Auth Buttons & Search - Right (hidden in pre-launch mode, except for admins) */}
          {showNavigation && (
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Search Icon Button (visible on desktop) */}
              <button
                onClick={() => navigate('/search')}
                className="hidden lg:flex p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                data-testid="nav-search-icon"
                aria-label="Suche"
                title="Suche"
              >
                <Search className="w-5 h-5" />
              </button>

              {!user ? (
                <>
                  <div className="hidden sm:flex items-center gap-3">
                    <button
                      onClick={() => navigate('/login')}
                      className="px-5 py-2 bg-[#F7B693] hover:bg-[#E9A07D] text-[#2B2142] font-bold rounded-full transition-all whitespace-nowrap uppercase tracking-wide text-sm"
                      data-testid="navbar-login-btn"
                    >
                      Login
                    </button>
                    <button
                      onClick={() => navigate('/register')}
                      className="px-5 py-2 bg-[#ddccfb] hover:bg-[#cbb6f7] text-[#2B2142] font-bold rounded-full transition-all whitespace-nowrap uppercase tracking-wide text-sm"
                      data-testid="navbar-register-btn"
                    >
                      Registrieren
                    </button>
                  </div>

                  <button
                    onClick={() => navigate('/register')}
                    className="sm:hidden px-4 py-2 bg-[#ddccfb] hover:bg-[#cbb6f7] text-[#2B2142] font-bold rounded-lg text-xs whitespace-nowrap uppercase tracking-wide"
                    data-testid="navbar-register-mobile"
                  >
                    Registrieren
                  </button>
                </>
              ) : (
                <>
                  {/* User Profile Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-2 px-3 py-2 hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
                        data-testid="user-menu-trigger"
                        aria-label="Benutzermenu"
                      >
                        {/* User Icon (Circle with initials or icon) */}
                        <div className="relative">
                          <div className="w-8 h-8 bg-[#F7B693] rounded-full flex items-center justify-center text-[#2B2142] font-semibold text-sm">
                            {user.email?.charAt(0).toUpperCase()}
                          </div>
                          {unreadTotal > 0 && (
                            <span
                              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#2B2142]"
                              aria-label={`${unreadTotal} ungelesene Nachrichten`}
                            >
                              {unreadTotal > 99 ? '99+' : unreadTotal}
                            </span>
                          )}
                        </div>
                        <span className="font-semibold hidden sm:inline truncate max-w-[150px] text-white">
                          {user.email}
                        </span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-auto min-w-[200px]">
                      {user.role === 'provider' && (
                        <DropdownMenuItem
                          onSelect={() => navigate('/provider/dashboard')}
                          data-testid="goto-dashboard"
                        >
                          <LayoutDashboard className="w-4 h-4 mr-2" />
                          Dashboard
                        </DropdownMenuItem>
                      )}
                      {user.role === 'customer' && (
                        <DropdownMenuItem
                          onSelect={() => navigate('/customer/dashboard')}
                          data-testid="goto-customer-dashboard"
                        >
                          <LayoutDashboard className="w-4 h-4 mr-2" />
                          Dashboard
                        </DropdownMenuItem>
                      )}
                      {user.role === 'admin' && (
                        <DropdownMenuItem
                          onSelect={() => navigate('/admin')}
                          data-testid="goto-admin"
                        >
                          <LayoutDashboard className="w-4 h-4 mr-2" />
                          Admin
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onSelect={onLogout}
                        className="text-red-600"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Abmelden
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Mobile Navigation Menu Button */}
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="lg:hidden p-2 hover:bg-white/10 rounded-lg touch-manipulation"
                    data-testid="mobile-menu-toggle"
                    aria-label="Navigationsmenü"
                  >
                    {mobileMenuOpen ? (
                      <X className="w-6 h-6 text-white" />
                    ) : (
                      <Menu className="w-6 h-6 text-white" />
                    )}
                  </button>
                </>
              )}

              {/* Mobile Menu Button for non-logged-in users */}
              {!user && (
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2 hover:bg-white/10 rounded-lg touch-manipulation"
                  data-testid="mobile-menu-toggle-guest"
                  aria-label="Navigationsmenü"
                >
                  {mobileMenuOpen ? (
                    <X className="w-6 h-6 text-white" />
                  ) : (
                    <Menu className="w-6 h-6 text-white" />
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Mobile Menu (hidden in pre-launch mode, except for admins) */}
        {showNavigation && mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/10 py-4 space-y-2" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => {
                  navigate(link.path);
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 font-semibold rounded-lg transition-colors ${
                  isActive(link.path) ? 'text-[#F7B693] bg-white/10' : 'text-white hover:bg-white/10'
                }`}
                data-testid={`mobile-${link.testId}`}
              >
                {link.label}
              </button>
            ))}

            {/* Suche im Mobile Menu */}
            <button
              onClick={() => {
                navigate('/search');
                setMobileMenuOpen(false);
              }}
              className={`w-full text-left px-4 py-3 font-semibold rounded-lg transition-colors ${
                isActive('/search') ? 'text-[#F7B693] bg-white/10' : 'text-white hover:bg-white/10'
              }`}
              data-testid="mobile-nav-search"
            >
              Suche
            </button>

            {!user && (
              <button
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3 text-white font-semibold hover:bg-white/10 rounded-lg"
                data-testid="mobile-login-btn"
              >
                Anmelden
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
